import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useCopyDocument,
  useDownloadDocumentContent,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
} from "@planzone/view-models";
import { Document as D, Identifiable } from "@planzone/dto";
import { MemberAvatar } from "@common/memberAvatar";
import { DownloadIcon } from "@common/assets/downloadIcon";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { TrashIcon } from "@common/trashIcon";
import { useModal } from "@root/client/modal";
import { DocumentIcon } from "@common/documentIcon";
import { Loader } from "@common/loader";
import { TransferIcon } from "@common/transferIcon";
import { EditIcon } from "@common/editIcon";
import { LockIcon } from "@common/lockIcon";
import { getDocumentType } from "@components/checkFileType";
import { ProjectModelsIcon } from "@common/assets/projectModelsIcon";
import { PenIcon } from "@common/penIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function formatFileSize(bytes: number) {
  if (bytes === 0) return "0 bytes";

  const sizes = ["bytes", "kb", "mb", "gb", "tb"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
}

function Document({
  document,
  templateFolder,
  currentFolder,
  select,
  edit,
}: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { pushView } = useModal();
  const { isRequestPending, downloadDocumentContent, file } =
    useDownloadDocumentContent(document);
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const canEditDocument = settings.canUseOnlyOffice;
  const { copyDocument } = useCopyDocument();

  const isValidForEdit = (document: Identifiable<D>) => {
    return getDocumentType(document.mimeType) !== undefined;
  };

  const addDocumentToTemplates = (document: Identifiable<D>) => {
    copyDocument({
      document,
      folder: templateFolder,
      type: "planzone",
      namespace: planzone.uuid,
    });
  };

  return (
    <div className={styles.document} onDoubleClick={select}>
      <div>
        <DocumentIcon className={styles.icon} mimeType={document.mimeType} />
        <div className={styles.column}>
          <p className={styles.name}>{document.name}</p>
          <p className={styles.size}>
            {formatFileSize(document.contentLength)}
          </p>
        </div>
      </div>
      <div>
        <div className={styles.avatar}>
          <MemberAvatar name={document.createdBy} avatarUrl="" />
        </div>
      </div>
      <div>
        <p className={styles.creationDate}>
          {new Date(document.createdDate).toLocaleDateString()}
        </p>
      </div>
      {isRequestPending ? (
        <div className={styles.loader}>
          <div>
            <Loader />
          </div>
        </div>
      ) : (
        <div className={styles.contextualMenu}>
          <SecureContextualMenu
            items={
              isValidForEdit(document)
                ? [
                    {
                      name: t("documentsPage.documentsPage.download"),
                      useCase: UseCases.downloadDocumentContent,
                      renderIcon: (className) => (
                        <DownloadIcon className={className} />
                      ),
                      act: () =>
                        downloadDocumentContent({ document, saveToDisk: true }),
                    },
                    {
                      name: t("documentsPage.documentsPage.rename"),
                      useCase: UseCases.updateDocument,
                      renderIcon: (className) => (
                        <PenIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.updateDocument,
                          data: {
                            document,
                            namespace: planzone.uuid,
                            type: "planzone",
                            folder: document.parent ?? currentFolder,
                          },
                        }),
                    },
                    {
                      name: t("documentsPage.documentsPage.move"),
                      useCase: UseCases.moveDocument,
                      renderIcon: (className) => (
                        <TransferIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.moveDocument,
                          data: {
                            document,
                            namespace: planzone.uuid,
                            type: "planzone",
                            projectName: planzone.name,
                          },
                        }),
                    },
                    {
                      name: t("documentsPage.documentsPage.edit"),
                      useCase: UseCases.downloadDocumentContent,
                      color: canEditDocument ? undefined : "#ff8e43",
                      renderIcon: (className) =>
                        canEditDocument ? (
                          <EditIcon className={className} />
                        ) : (
                          <LockIcon
                            className={classNames(className, styles.editIcon)}
                          />
                        ),
                      act: () => canEditDocument && edit(),
                    },
                    {
                      name: t("documentsPage.documentsPage.addToTemplates"),
                      useCase: UseCases.downloadFolderContent,
                      renderIcon: (className) => (
                        <ProjectModelsIcon className={className} />
                      ),
                      act: () => addDocumentToTemplates(document),
                    },
                    {
                      name: t("documentsPage.documentsPage.remove"),
                      useCase: UseCases.removeDocument,
                      color: "#db5f63",
                      renderIcon: (className) => (
                        <TrashIcon
                          className={classNames(className, styles.removeIcon)}
                        />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.removeDocument,
                          data: document,
                        }),
                    },
                  ]
                : [
                    {
                      name: t("documentsPage.documentsPage.download"),
                      useCase: UseCases.downloadDocumentContent,
                      renderIcon: (className) => (
                        <DownloadIcon className={className} />
                      ),
                      act: () =>
                        downloadDocumentContent({ document, saveToDisk: true }),
                    },
                    {
                      name: t("documentsPage.documentsPage.rename"),
                      useCase: UseCases.updateDocument,
                      renderIcon: (className) => (
                        <PenIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.updateDocument,
                          data: {
                            document,
                            namespace: planzone.uuid,
                            type: "planzone",
                            folder: document.parent ?? currentFolder,
                          },
                        }),
                    },
                    {
                      name: t("documentsPage.documentsPage.move"),
                      useCase: UseCases.moveDocument,
                      renderIcon: (className) => (
                        <TransferIcon className={className} />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.moveDocument,
                          data: {
                            document,
                            namespace: planzone.uuid,
                            type: "planzone",
                            projectName: planzone.name,
                          },
                        }),
                    },
                    {
                      name: t("documentsPage.documentsPage.addToTemplates"),
                      useCase: UseCases.downloadFolderContent,
                      renderIcon: (className) => (
                        <ProjectModelsIcon className={className} />
                      ),
                      act: () => addDocumentToTemplates(document),
                    },
                    {
                      name: t("documentsPage.documentsPage.remove"),
                      useCase: UseCases.removeDocument,
                      color: "#db5f63",
                      renderIcon: (className) => (
                        <TrashIcon
                          className={classNames(className, styles.removeIcon)}
                        />
                      ),
                      act: () =>
                        pushView({
                          key: UseCases.removeDocument,
                          data: document,
                        }),
                    },
                  ]
            }
          />
        </div>
      )}
    </div>
  );
}

export { Document };
